import { useEffect } from 'react';

/**
 * Hook to position a dropdown menu so it's always fully visible within the viewport.
 * Adjusts position if the menu would overflow screen bounds.
 */
export function useMenuPosition(
  isOpen: boolean,
  triggerRef: React.RefObject<HTMLElement | null>,
  menuRef: React.RefObject<HTMLElement | null>
) {
  useEffect(() => {
    if (!isOpen || !triggerRef.current || !menuRef.current) return;

    const trigger = triggerRef.current;
    const menu = menuRef.current;

    // Reset any previous custom positioning
    menu.style.removeProperty('bottom');
    menu.style.removeProperty('left');
    menu.style.removeProperty('right');
    menu.style.removeProperty('top');
    menu.style.removeProperty('transform');
    menu.style.removeProperty('margin-bottom');
    menu.style.removeProperty('margin-top');
    menu.style.removeProperty('max-height');
    menu.style.removeProperty('overflow-y');
    menu.style.removeProperty('width');

    // Wait for menu to fully render before measuring
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const triggerRect = trigger.getBoundingClientRect();
        const menuRect = menu.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

      // Vertical positioning
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      const menuHeight = menuRect.height;

      if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
        // Not enough space below, but more space above - flip to above
        menu.style.bottom = '100%';
        menu.style.top = 'auto';
        menu.style.marginBottom = '4px';
        menu.style.marginTop = '0';
      } else if (spaceBelow < menuHeight) {
        // Not enough space below or above - constrain height and scroll
        const maxHeight = Math.max(spaceBelow, spaceAbove) - 20;
        menu.style.maxHeight = `${maxHeight}px`;
        menu.style.overflowY = 'auto';
      }

      // Horizontal positioning - check if menu overflows viewport
      const menuLeft = menuRect.left;
      const menuRight = menuRect.right;

      if (menuRight > viewportWidth) {
        // Menu overflows right edge of viewport
        const overflow = menuRight - viewportWidth;
        const shift = overflow + 8; // Add 8px padding from edge

        // Check if shifting would push it off the left edge
        if (menuLeft - shift < 8) {
          // Constrain to viewport with padding on both sides
          menu.style.right = '8px';
          menu.style.left = '8px';
          menu.style.width = 'auto';
        } else {
          // Shift left by the overflow amount
          menu.style.transform = `translateX(-${shift}px)`;
        }
      } else if (menuLeft < 0) {
        // Menu overflows left edge of viewport
        const shift = -menuLeft + 8;
        menu.style.transform = `translateX(${shift}px)`;
      }
      });
    });
  }, [isOpen, triggerRef, menuRef]);
}
